import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { doc, onSnapshot, query, collection, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase.js';
import { COL } from '@bytecore/firestore-schema';
import { useAuthStore } from './auth.js';
import { fnUrl } from '../functions.js';

async function callFn(name, body, staffToken) {
  const res = await fetch(fnUrl(name), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(staffToken ? { Authorization: `Bearer ${staffToken}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!data.ok) {
    const err = new Error(data.error ?? 'Unknown error');
    err.existingShiftId = data.existingShiftId;
    throw err;
  }
  return data;
}

export const useShiftStore = defineStore('pos-shift', () => {
  const currentShift = ref(null);
  const loading = ref(true);
  let _unsub = null;

  const currentShiftId = ref(localStorage.getItem('pos:currentShiftId') ?? null);

  // ── Subscribe to a known shift doc ────────────────────────────────────────
  function subscribeToShift(shiftId) {
    _unsub?.();
    currentShiftId.value = shiftId;
    localStorage.setItem('pos:currentShiftId', shiftId);

    _unsub = onSnapshot(
      doc(db, COL.CASH_SHIFTS, shiftId),
      (snap) => {
        currentShift.value = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        loading.value = false;
      },
      (err) => {
        console.warn('[shift] snapshot error:', err.code);
        loading.value = false;
      },
    );
  }

  function clearShift() {
    _unsub?.();
    _unsub = null;
    currentShift.value = null;
    currentShiftId.value = null;
    localStorage.removeItem('pos:currentShiftId');
  }

  // ── Init — auto-subscribe if a shift was stored ───────────────────────────
  async function init() {
    const savedId = localStorage.getItem('pos:currentShiftId');
    if (savedId) {
      subscribeToShift(savedId);
    } else {
      // Check if there's an open shift in Firestore (e.g. after app reinstall).
      try {
        const snap = await getDocs(
          query(collection(db, COL.CASH_SHIFTS), where('status', '==', 'open'), limit(1)),
        );
        if (!snap.empty) {
          subscribeToShift(snap.docs[0].id);
        } else {
          loading.value = false;
        }
      } catch {
        loading.value = false;
      }
    }
  }

  // ── Open shift ────────────────────────────────────────────────────────────
  async function openShift(openingFloat) {
    const auth = useAuthStore();
    const token = await auth.staffUser?.getIdToken();
    const result = await callFn('posShiftOpen', { openingFloat }, token);
    subscribeToShift(result.shiftId);
    return result.shiftId;
  }

  // ── Close shift ───────────────────────────────────────────────────────────
  async function closeShift(closingFloat) {
    const auth = useAuthStore();
    const token = await auth.staffUser?.getIdToken();
    const result = await callFn('posShiftClose', {
      shiftId: currentShiftId.value,
      closingFloat,
    }, token);
    clearShift();
    return result.report;
  }

  const isOpen = computed(() => currentShift.value?.status === 'open');
  const shiftId = computed(() => currentShift.value?.id ?? null);
  const openingFloat = computed(() => currentShift.value?.openingFloat ?? 0);

  return {
    currentShift, currentShiftId, loading,
    isOpen, shiftId, openingFloat,
    init, subscribeToShift, clearShift, openShift, closeShift,
  };
});
